const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';

function buildPrompt(rawText) {
  return [
    {
      role: 'user',
      parts: [{ text: `You are an information extraction engine for academic or professional certificates. Extract and return a STRICT JSON object with these keys only: { "studentName": string, "rollNumber": string, "certificateType": string, "issueDate": string, "marks": string }.

Rules:
- If a field is unknown or not present, return an empty string for that field.
- Prefer the recipient name (the person's name) for studentName.
- rollNumber may also appear as Serial Number / Certificate ID / Candidate ID / Registration No.
- certificateType should be concise like "Certified Developer" or "Bachelor of Technology".
- issueDate can be any detected date or validity start; if only validity end is present, leave empty.

Certificate OCR text:
---
${rawText}
---

Return ONLY the JSON, no extra text.` }]
    }
  ];
}

export async function enhanceOcrWithGemini(rawText) {
  if (!rawText || !GEMINI_API_KEY) {
    return null;
  }

  try {
    const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
    const body = {
      contents: buildPrompt(rawText)
    };

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(`Gemini request failed ${res.status}: ${err?.slice(0,200)}`);
    }

    const data = await res.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
    if (!text) return null;

    // Attempt to parse JSON from model response
    let jsonText = text.trim();
    // Strip fences if present
    jsonText = jsonText.replace(/^```json\n?|```$/g, '');
    const parsed = JSON.parse(jsonText);

    const normalized = {
      studentName: String(parsed.studentName || '').trim(),
      rollNumber: String(parsed.rollNumber || '').trim(),
      certificateType: String(parsed.certificateType || '').trim(),
      issueDate: String(parsed.issueDate || '').trim(),
      marks: String(parsed.marks || '').trim(),
    };
    return normalized;
  } catch (e) {
    console.warn('Gemini postprocess failed:', e.message || e);
    return null;
  }
}

export default enhanceOcrWithGemini;



// Extract structured metadata directly from an image using Gemini Vision
export async function extractWithGeminiVision(file, userPrompt) {
  if (!GEMINI_API_KEY || !file) return null;

  try {
    const arrayBuffer = await file.arrayBuffer();
    const base64Data = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

    const systemPrompt = (userPrompt || '').trim() ||
      'Extract certificate details as strict JSON with keys {"studentName","rollNumber","certificateType","issueDate","marks"}. Return ONLY JSON.';

    const body = {
      contents: [
        {
          role: 'user',
          parts: [
            { text: systemPrompt },
            {
              inline_data: {
                mime_type: file.type || 'image/png',
                data: base64Data
              }
            }
          ]
        }
      ]
    };

    const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!res.ok) {
      const err = await res.text().catch(() => '');
      throw new Error(`Gemini Vision failed ${res.status}: ${err?.slice(0,200)}`);
    }

    const data = await res.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
    if (!text) return null;

    let jsonText = text.trim();
    jsonText = jsonText.replace(/^```json\n?|```$/g, '');
    const parsed = JSON.parse(jsonText);

    return {
      studentName: String(parsed.studentName || '').trim(),
      rollNumber: String(parsed.rollNumber || '').trim(),
      certificateType: String(parsed.certificateType || '').trim(),
      issueDate: String(parsed.issueDate || '').trim(),
      marks: String(parsed.marks || '').trim(),
      raw: ''
    };
  } catch (e) {
    console.warn('Gemini Vision OCR failed:', e.message || e);
    return null;
  }
}

